import Phaser from "phaser";
import { TOPDOWN_TILE } from "../projection/TopDownProjection";

/**
 * Procedurally-generated placeholder tileset for top-down zones.
 * Registered as a 5-frame spritesheet under the key returned by `topdownTilesetKey()`.
 *
 * Frames (1-indexed in TMJ tile IDs; 0-indexed here):
 *   0 (TMJ 1) → grass
 *   1 (TMJ 2) → cobblestone
 *   2 (TMJ 3) → dirt path
 *   3 (TMJ 4) → water
 *   4 (TMJ 5) → wall
 */
export const TOPDOWN_TILESET_KEY = "topdown-base";

const FRAMES = 5;

export function ensureTopDownTextures(scene: Phaser.Scene): void {
  if (scene.textures.exists(TOPDOWN_TILESET_KEY)) return;

  const W = TOPDOWN_TILE * FRAMES;
  const H = TOPDOWN_TILE;
  const g = scene.make.graphics({ x: 0, y: 0 }, false);

  drawGrass(g, 0);
  drawCobble(g, TOPDOWN_TILE);
  drawDirtPath(g, TOPDOWN_TILE * 2);
  drawWater(g, TOPDOWN_TILE * 3);
  drawWall(g, TOPDOWN_TILE * 4);

  g.generateTexture(TOPDOWN_TILESET_KEY, W, H);
  g.destroy();

  // Convert generated texture into a spritesheet so frame indexing works.
  const tex = scene.textures.get(TOPDOWN_TILESET_KEY);
  for (let i = 0; i < FRAMES; i++) {
    tex.add(i, 0, i * TOPDOWN_TILE, 0, TOPDOWN_TILE, TOPDOWN_TILE);
  }
}

function drawGrass(g: Phaser.GameObjects.Graphics, ox: number): void {
  g.fillStyle(0x3f7a3a, 1);
  g.fillRect(ox, 0, TOPDOWN_TILE, TOPDOWN_TILE);
  g.fillStyle(0x4f8a4a, 1);
  for (let i = 0; i < 8; i++) {
    const x = ox + ((i * 7 + 3) % TOPDOWN_TILE);
    const y = (i * 11 + 5) % TOPDOWN_TILE;
    g.fillRect(x, y, 2, 2);
  }
  g.lineStyle(1, 0x2a5a28, 0.4);
  g.strokeRect(ox + 0.5, 0.5, TOPDOWN_TILE - 1, TOPDOWN_TILE - 1);
}

function drawCobble(g: Phaser.GameObjects.Graphics, ox: number): void {
  g.fillStyle(0x8a8a82, 1);
  g.fillRect(ox, 0, TOPDOWN_TILE, TOPDOWN_TILE);
  g.lineStyle(1, 0x5a5a52, 0.7);
  // Brick-ish pattern: 4 cells per tile (offset rows for variety)
  for (let row = 0; row < 4; row++) {
    const offsetX = (row % 2) * 4;
    for (let col = 0; col < 4; col++) {
      const x = ox + col * 8 + offsetX - (offsetX > 0 && col === 3 ? 8 : 0);
      const y = row * 8;
      g.strokeRect(x + 0.5, y + 0.5, 7, 7);
    }
  }
}

function drawDirtPath(g: Phaser.GameObjects.Graphics, ox: number): void {
  g.fillStyle(0x8a6a3a, 1);
  g.fillRect(ox, 0, TOPDOWN_TILE, TOPDOWN_TILE);
  g.fillStyle(0x6a4a20, 0.7);
  // Pebbles
  for (let i = 0; i < 6; i++) {
    const x = ox + ((i * 13 + 4) % TOPDOWN_TILE);
    const y = (i * 9 + 2) % TOPDOWN_TILE;
    g.fillCircle(x, y, 1.5);
  }
}

function drawWater(g: Phaser.GameObjects.Graphics, ox: number): void {
  g.fillStyle(0x1a5a8a, 1);
  g.fillRect(ox, 0, TOPDOWN_TILE, TOPDOWN_TILE);
  g.lineStyle(1, 0x3a8aba, 0.5);
  for (let i = 0; i < 4; i++) {
    const y = 4 + i * 8;
    g.beginPath();
    g.moveTo(ox + 2, y);
    g.lineTo(ox + 10, y + 2);
    g.lineTo(ox + 18, y);
    g.lineTo(ox + 26, y + 2);
    g.strokePath();
  }
}

function drawWall(g: Phaser.GameObjects.Graphics, ox: number): void {
  g.fillStyle(0x5a4a3a, 1);
  g.fillRect(ox, 0, TOPDOWN_TILE, TOPDOWN_TILE);
  g.fillStyle(0x4a3a2a, 1);
  g.fillRect(ox + 2, 2, TOPDOWN_TILE - 4, TOPDOWN_TILE - 4);
  g.lineStyle(1, 0x2a1a10, 1);
  g.strokeRect(ox + 0.5, 0.5, TOPDOWN_TILE - 1, TOPDOWN_TILE - 1);
  // Cross pattern (suggests wood beam / stall)
  g.lineStyle(2, 0x3a2a1a, 0.8);
  g.lineBetween(ox + 4, 4, ox + TOPDOWN_TILE - 4, TOPDOWN_TILE - 4);
  g.lineBetween(ox + TOPDOWN_TILE - 4, 4, ox + 4, TOPDOWN_TILE - 4);
}
